import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  getUserArticleRedeemList,
  getUserArticleRedeemById,
  createUserArticleRedeem,
  updateUserArticleRedeem,
  deleteUserArticleRedeem
} from '../../api/userArticleRedeem';
import { message } from 'antd';

export const fetchUserArticleRedeemList = createAsyncThunk(
  'userArticleRedeem/fetchList',
  async (queryDTO = {}, { rejectWithValue }) => {
    try {
      const response = await getUserArticleRedeemList(queryDTO);
      if (response && response.code === 200) {
        return {
          data: response.data,
          params: queryDTO
        };
      }
      return rejectWithValue(response?.message || '获取用户付费文章服务兑换列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取用户付费文章服务兑换列表失败');
    }
  }
);

export const fetchUserArticleRedeemById = createAsyncThunk(
  'userArticleRedeem/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getUserArticleRedeemById(id);
      if (response && response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response?.message || '获取用户付费文章服务兑换详情失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取用户付费文章服务兑换详情失败');
    }
  }
);

export const createUserArticleRedeemAsync = createAsyncThunk(
  'userArticleRedeem/create',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await createUserArticleRedeem(data);
      if (response && response.code === 200) {
        message.success('创建用户付费文章服务兑换记录成功');
        dispatch(fetchUserArticleRedeemList({ page: 1, size: 10 }));
        return response.data;
      }
      return rejectWithValue(response?.message || '创建用户付费文章服务兑换记录失败');
    } catch (error) {
      const errorMessage = error.response?.data?.message || '创建用户付费文章服务兑换记录失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateUserArticleRedeemAsync = createAsyncThunk(
  'userArticleRedeem/update',
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await updateUserArticleRedeem(id, data);
      if (response && response.code === 200) {
        message.success('更新用户付费文章服务兑换记录成功');
        dispatch(fetchUserArticleRedeemList({ page: 1, size: 10 }));
        return response.data;
      }
      return rejectWithValue(response?.message || '更新用户付费文章服务兑换记录失败');
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新用户付费文章服务兑换记录失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteUserArticleRedeemAsync = createAsyncThunk(
  'userArticleRedeem/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await deleteUserArticleRedeem(id);
      if (response && response.code === 200) {
        message.success('删除用户付费文章服务兑换记录成功');
        dispatch(fetchUserArticleRedeemList({ page: 1, size: 10 }));
        return response.data;
      }
      return rejectWithValue(response?.message || '删除用户付费文章服务兑换记录失败');
    } catch (error) {
      const errorMessage = error.response?.data?.message || '删除用户付费文章服务兑换记录失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  userArticleRedeemList: {
    data: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    searchParams: {}
  },
  currentUserArticleRedeem: null,
  operationStatus: {
    loading: false,
    success: false,
    error: null
  }
};

const userArticleRedeemSlice = createSlice({
  name: 'userArticleRedeem',
  initialState,
  reducers: {
    clearCurrentUserArticleRedeem: (state) => {
      state.currentUserArticleRedeem = null;
    },
    setSearchParams: (state, action) => {
      const { page, size, ...searchParams } = action.payload;
      if (page !== undefined) {
        state.userArticleRedeemList.current = page;
      }
      if (size !== undefined) {
        state.userArticleRedeemList.size = size;
      }
      state.userArticleRedeemList.searchParams = {
        ...state.userArticleRedeemList.searchParams,
        ...searchParams
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserArticleRedeemList.pending, (state) => {
        state.userArticleRedeemList.loading = true;
      })
      .addCase(fetchUserArticleRedeemList.fulfilled, (state, action) => {
        state.userArticleRedeemList.loading = false;
        state.userArticleRedeemList.data = action.payload.data.records || [];
        state.userArticleRedeemList.total = action.payload.data.total || 0;
        state.userArticleRedeemList.current = action.payload.data.current || action.payload.params.page || 1;
        if (action.payload.params) {
          state.userArticleRedeemList.searchParams = {
            ...state.userArticleRedeemList.searchParams,
            ...action.payload.params
          };
        }
      })
      .addCase(fetchUserArticleRedeemList.rejected, (state, action) => {
        state.userArticleRedeemList.loading = false;
        message.error(action.payload || '获取用户付费文章服务兑换列表失败');
      })
      .addCase(fetchUserArticleRedeemById.pending, (state) => {
        state.operationStatus.loading = true;
      })
      .addCase(fetchUserArticleRedeemById.fulfilled, (state, action) => {
        state.operationStatus.loading = false;
        state.currentUserArticleRedeem = action.payload;
      })
      .addCase(fetchUserArticleRedeemById.rejected, (state, action) => {
        state.operationStatus.loading = false;
        message.error(action.payload || '获取用户付费文章服务兑换详情失败');
      })
      .addCase(createUserArticleRedeemAsync.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
      })
      .addCase(createUserArticleRedeemAsync.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(createUserArticleRedeemAsync.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload;
      })
      .addCase(updateUserArticleRedeemAsync.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
      })
      .addCase(updateUserArticleRedeemAsync.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(updateUserArticleRedeemAsync.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload;
      })
      .addCase(deleteUserArticleRedeemAsync.pending, (state) => {
        state.operationStatus.loading = true;
      })
      .addCase(deleteUserArticleRedeemAsync.fulfilled, (state) => {
        state.operationStatus.loading = false;
      })
      .addCase(deleteUserArticleRedeemAsync.rejected, (state) => {
        state.operationStatus.loading = false;
      });
  }
});

export const { clearCurrentUserArticleRedeem, setSearchParams } = userArticleRedeemSlice.actions;
export default userArticleRedeemSlice.reducer;